import type {
  PromptWithMeta,
  SiteContext,
} from '@packages/systems/ai/copilot/shared';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import type {AIAssistantOpenSource} from '@packages/domains/ai-assistant/data-types';

export type AIAssistantSurfaceMode = 'left' | 'right' | 'floating';

export const AIA_ASSISTANT_DEFAULT_SURFACE_MODE: AIAssistantSurfaceMode =
  'right';
export const AIA_RELOCATION_SURFACE_WIDTH = 280;
/**
 * Bounds for the user-draggable width of the docked AIA surface. The minimum is
 * the historical fixed width, so a docked panel never gets narrower than it was
 * before it became resizable. (EXT-162)
 */
export const AIA_RELOCATION_SURFACE_MIN_WIDTH = AIA_RELOCATION_SURFACE_WIDTH;
export const AIA_RELOCATION_SURFACE_MAX_WIDTH = 560;
/**
 * Gap between the docked AIA surface and the inset designer "card", and
 * between the card and the other viewport edges. (CODE-2595)
 */
export const AIA_RELOCATION_WORKSPACE_INSET = 8;
/**
 * Gap between the docked agent-chat (workflows) surface and the inset "card" —
 * tighter than the AIA gutter. Only the dock↔card side; the card's other
 * viewport-edge gutters keep AIA_RELOCATION_WORKSPACE_INSET.
 */
export const AGENT_CHAT_DOCK_GUTTER = 4;
/**
 * Horizontal space reserved between the viewport edge and the designer card
 * edge on the docked side: surface width + workspace inset (288px), not the
 * bare panel width.
 */
export const AIA_RELOCATION_WIDTH_CSS_VAR =
  '--wf-designer--aiaRelocationSurfaceWidth';
/**
 * Set to 8px on <html> only while the AIA surface is docked left/right;
 * absent otherwise so LESS consumers can use `var(..., 0px)` defaults.
 */
export const AIA_RELOCATION_WORKSPACE_INSET_CSS_VAR =
  '--wf-designer--aiaRelocationWorkspaceInset';
export const AIA_RELOCATION_LEFT_CLASS_NAME =
  'aia-assistant-relocation-left-open';
export const AIA_RELOCATION_RIGHT_CLASS_NAME =
  'aia-assistant-relocation-right-open';
/**
 * Set on <html> while the AIA surface is docked in a workflow without the
 * designer rails (e.g. the CMS Data Manager), where no avoid-*-sidebar
 * classes reserve space beside the surface, so the stylesheet reserves the
 * surface margin directly. (CODE-3576)
 */
export const AIA_RELOCATION_STANDALONE_CLASS_NAME =
  'aia-assistant-relocation-standalone';

export type CopilotAIJSMeta = {
  nodeId: string;
  siteId?: string;
  aidaRequestId: string;
};

export type CopilotAIPromptMeta = {
  nodeString: string;
  siteId?: string;
};

export type PromptWithTimestamp = PromptWithMeta<CopilotAIPromptMeta> & {
  timestamp: number;
};

export type PromptMessages = Array<PromptWithTimestamp> | [];

export type PromptRequest = {
  nodeIdPath: NativeId[];
  nodeString: string;
  cancel: boolean;
  requestId: string;
  id: string;
};

export type PromptRequests = Array<PromptRequest>;

export type AssistantState = {
  isCopilotVisible: boolean;
  isAiAssistantVisible: boolean;
  requests: PromptRequests;
  whtmlPlaygroundIsOpen: boolean;
  whtmlPlaygroundInput: {
    whtml: string;
    css: string;
  } | null;
  currentCopilotRequestId: string | null;
  lastCopilotRequestId: string | null;
  /**
   * Set of unique StyleMap selectors that we think need to be updated,
   * based on recent style changes
   */
  dirtyStyleMapSelectors: Set<string>;
  /**
   * The SiteContext fetched from the server. This has site information related to AI features.
   */
  siteContext: SiteContext | null;
  inputFocusRequested?: boolean;
  aiAssistantOpenSource: AIAssistantOpenSource | null;
  aiAssistantSurfaceMode: AIAssistantSurfaceMode;
  aiAssistantSurfaceModeWasPersisted: boolean;
  /**
   * Whether the placement above is the user's own choice — restored from a
   * previous visit or picked this session — rather than a default. Distinct
   * from `aiAssistantSurfaceModeWasPersisted`, which reports only what was
   * loaded at boot and feeds analytics.
   */
  aiAssistantSurfaceModeSelectedByUser?: boolean;
  /** User-dragged width of the docked surface, clamped to the bounds above. */
  aiaDockedSurfaceWidth: number;
  aiaTriggeredPostAisb: boolean;
};
